'use client';

// useFileUpload - TanStack Query mutation for uploading and extracting file content

import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { FileRejection } from 'react-dropzone';
import { saveSessionToStorage, getOrCreateClientSession } from '../lib/session';
import { getReferral, clearReferral } from '../lib/referralTracking';
import type { UploadedFile, FileStatus, ProcessingMethod } from '../features/content-input';
import { validateFile, type FileValidationErrorCode } from '../features/content-input/validation';

/**
 * Response from the file upload API
 */
interface FileUploadResponse {
  data: {
    contentId: string;
    botId: string;
    fileName: string;
    fileSize: number;
    sourceType: 'file';
    preview: string;
    characterCount: number;
    processingMethod: 'text' | 'ai';
  };
}

/**
 * Error response from the file upload API
 */
interface FileUploadError {
  error: {
    code: string;
    message: string;
  };
}

/**
 * Maps API error codes to i18n error keys
 */
function mapErrorCodeToI18nKey(code: string): FileValidationErrorCode | 'extractionFailed' | 'uploadFailed' {
  switch (code) {
    case 'FILE_TOO_LARGE':
      return 'tooLarge';
    case 'UNSUPPORTED_TYPE':
      return 'unsupportedType';
    case 'EXTRACTION_FAILED':
      return 'extractionFailed';
    default:
      return 'uploadFailed';
  }
}

/**
 * Ensures session is synced between server cookie and localStorage BEFORE upload.
 */
async function ensureSessionSync(): Promise<void> {
  try {
    const localSession = getOrCreateClientSession();

    const response = await fetch('/api/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId: localSession }),
    });

    const data = await response.json();
    if (data.data?.sessionId) {
      saveSessionToStorage(data.data.sessionId);
    }
  } catch {
    console.warn('[session] Pre-upload session sync failed');
  }
}

/**
 * Options for file upload
 */
interface UploadOptions {
  botId?: string | undefined;
  useAI?: boolean | undefined;
}

/**
 * Uploads a single file to the API
 * @param file - The file to upload
 * @param options - Upload options including botId and useAI flag
 */
async function uploadFile(file: File, options?: UploadOptions): Promise<FileUploadResponse> {
  await ensureSessionSync();

  const formData = new FormData();
  formData.append('file', file);
  if (options?.botId) {
    formData.append('botId', options.botId);
  }

  // Story 6.5: Add referral attribution for new bot creation
  const isNewBot = !options?.botId;
  const referrerBotId = isNewBot ? getReferral() : null;
  if (referrerBotId) {
    formData.append('referrerBotId', referrerBotId);
  }

  // Build URL with optional useAI query parameter
  const url = options?.useAI ? '/api/bots/upload?useAI=true' : '/api/bots/upload';

  const response = await fetch(url, {
    method: 'POST',
    body: formData,
  });

  const data = await response.json();

  if (!response.ok) {
    throw data as FileUploadError;
  }

  // Story 6.5: Clear referral after successful bot creation
  if (isNewBot && referrerBotId) {
    clearReferral();
  }

  return data as FileUploadResponse;
}

/**
 * Generates a unique ID for tracking uploaded files
 */
function generateFileId(): string {
  return `file-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

export interface UseFileUploadOptions {
  /** Bot ID to associate uploads with */
  botId?: string;
  /** Callback when a file upload completes successfully */
  onSuccess?: (file: UploadedFile) => void;
  /** Callback when a file upload fails */
  onError?: (file: UploadedFile, error: FileUploadError) => void;
}

export interface UseFileUploadReturn {
  /** List of uploaded files with their status */
  files: UploadedFile[];
  /** Whether any upload is in progress */
  isUploading: boolean;
  /** Upload files (validates and uploads each file) */
  uploadFiles: (files: File[]) => void;
  /** Handle files rejected by dropzone */
  handleRejectedFiles: (rejections: FileRejection[]) => void;
  /** Remove a file from the list by local ID */
  removeFile: (fileId: string) => void;
  /** Remove a file from the list by database contentId */
  removeByContentId: (contentId: string) => void;
  /** Clear all files */
  clearFiles: () => void;
  /** Total character count across all extracted files */
  totalCharacterCount: number;
  /** Bot ID created for file uploads (shared across all files) */
  botId: string | null;
  /** Re-process a PDF file with AI extraction */
  processWithAI: (fileId: string) => void;
}

/**
 * Hook for managing file uploads with TanStack Query
 *
 * Features:
 * - Client-side validation before upload
 * - Progress tracking per file
 * - Optimistic updates for better UX
 * - Error handling with i18n support
 * - Multiple file uploads in parallel
 *
 * @example
 * ```tsx
 * const { files, isUploading, uploadFiles, removeFile } = useFileUpload({
 *   onSuccess: (file) => console.log('Uploaded:', file.fileName),
 * });
 *
 * return (
 *   <>
 *     <FileUploadZone
 *       onFilesAccepted={uploadFiles}
 *       disabled={isUploading}
 *     />
 *     <FilePreviewList files={files} onRemove={removeFile} />
 *   </>
 * );
 * ```
 */
export function useFileUpload(options?: UseFileUploadOptions): UseFileUploadReturn {
  const queryClient = useQueryClient();
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [createdBotId, setCreatedBotId] = useState<string | null>(options?.botId ?? null);
  // Store original File objects for potential re-processing with AI
  const [originalFiles, setOriginalFiles] = useState<Map<string, File>>(new Map());

  // Single file upload mutation
  const uploadMutation = useMutation({
    mutationFn: ({ file, useAI }: { file: File; fileId: string; useAI?: boolean }) =>
      uploadFile(file, { botId: createdBotId ?? options?.botId, useAI }),
    onMutate: async ({ file, fileId, useAI }) => {
      // Check if file already exists (re-processing scenario)
      setFiles((prev) => {
        const existingIndex = prev.findIndex((f) => f.id === fileId);
        if (existingIndex >= 0) {
          // Update existing file status for re-processing
          return prev.map((f) =>
            f.id === fileId
              ? { ...f, status: (useAI ? 'ai-processing' : 'uploading') as FileStatus }
              : f
          );
        }
        // Add new file with uploading status
        const newFile: UploadedFile = {
          id: fileId,
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
          status: 'uploading' as FileStatus,
        };
        return [...prev, newFile];
      });
    },
    onSuccess: (response, { fileId }) => {
      // Track the bot ID from the first successful upload
      if (response.data.botId && !createdBotId) {
        setCreatedBotId(response.data.botId);
      }

      // Store the updated file data for the callback
      let updatedFileData: UploadedFile | null = null;

      // Update file with extraction result, contentId, and processing method
      setFiles((prev) => {
        const updated = prev.map((f) => {
          if (f.id === fileId) {
            const updatedFile: UploadedFile = {
              ...f,
              status: 'ready' as FileStatus,
              preview: response.data.preview,
              contentId: response.data.contentId,
              processingMethod: response.data.processingMethod,
            };
            updatedFileData = updatedFile;
            return updatedFile;
          }
          return f;
        });
        return updated;
      });

      // Invalidate content sources query to refresh the unified list
      if (response.data.botId) {
        queryClient.invalidateQueries({ queryKey: ['contentSources', response.data.botId] });
      }

      // Call onSuccess after state update using queueMicrotask to ensure it's outside render
      queueMicrotask(() => {
        if (updatedFileData) {
          options?.onSuccess?.(updatedFileData);
        }
      });
    },
    onError: (error: FileUploadError, { fileId }) => {
      const errorKey = mapErrorCodeToI18nKey(error.error.code);

      // Store the failed file data for the callback
      let failedFileData: UploadedFile | null = null;

      setFiles((prev) => {
        const updated = prev.map((f) => {
          if (f.id === fileId) {
            const updatedFile = {
              ...f,
              status: 'error' as FileStatus,
              errorMessage: errorKey,
            };
            failedFileData = updatedFile;
            return updatedFile;
          }
          return f;
        });
        return updated;
      });

      // Call onError after state update using queueMicrotask to ensure it's outside render
      queueMicrotask(() => {
        if (failedFileData) {
          options?.onError?.(failedFileData, error);
        }
      });
    },
  });

  // Upload multiple files
  const uploadFiles = useCallback(
    (newFiles: File[]) => {
      newFiles.forEach((file) => {
        // Validate file client-side first
        const validation = validateFile(file);

        if (!validation.isValid) {
          // Add file with error status
          const fileId = generateFileId();
          const errorFile: UploadedFile = {
            id: fileId,
            fileName: file.name,
            fileSize: file.size,
            mimeType: file.type,
            status: 'error',
            errorMessage: validation.errorCode ?? 'uploadFailed',
          };
          setFiles((prev) => [...prev, errorFile]);
          return;
        }

        // Upload valid file
        const fileId = generateFileId();
        // Store original File for potential AI re-processing
        setOriginalFiles((prev) => new Map(prev).set(fileId, file));
        uploadMutation.mutate({ file, fileId });
      });
    },
    [uploadMutation]
  );

  // Handle files rejected by dropzone
  const handleRejectedFiles = useCallback((rejections: FileRejection[]) => {
    rejections.forEach((rejection) => {
      const fileId = generateFileId();
      const errorCode = rejection.errors[0]?.code;

      let errorMessage: string;
      if (errorCode === 'file-too-large') {
        errorMessage = 'tooLarge';
      } else if (errorCode === 'file-invalid-type') {
        errorMessage = 'unsupportedType';
      } else {
        errorMessage = 'uploadFailed';
      }

      const errorFile: UploadedFile = {
        id: fileId,
        fileName: rejection.file.name,
        fileSize: rejection.file.size,
        mimeType: rejection.file.type,
        status: 'error',
        errorMessage,
      };
      setFiles((prev) => [...prev, errorFile]);
    });
  }, []);

  // Remove a file from the list and delete from database
  const removeFile = useCallback(
    async (fileId: string) => {
      // Find the file to get its contentId
      const fileToRemove = files.find((f) => f.id === fileId);

      // Remove from local state immediately
      setFiles((prev) => prev.filter((f) => f.id !== fileId));
      // Clean up stored original File
      setOriginalFiles((prev) => {
        const newMap = new Map(prev);
        newMap.delete(fileId);
        return newMap;
      });

      // If file has a contentId, delete from database
      if (fileToRemove?.contentId && createdBotId) {
        try {
          await fetch(`/api/bots/${createdBotId}/content/${fileToRemove.contentId}`, {
            method: 'DELETE',
          });
          // Invalidate content sources to refresh unified panel
          queryClient.invalidateQueries({ queryKey: ['contentSources', createdBotId] });
        } catch (error) {
          console.error('[useFileUpload] Failed to delete content from API:', error);
        }
      }
    },
    [files, createdBotId, queryClient]
  );

  // Remove a file by its database contentId (for syncing with unified panel)
  const removeByContentId = useCallback((contentId: string) => {
    // Find the file to clean up its originalFile entry
    setFiles((prev) => {
      const fileToRemove = prev.find((f) => f.contentId === contentId);
      if (fileToRemove) {
        // Clean up stored original File to prevent memory leak
        setOriginalFiles((prevOriginals) => {
          const newMap = new Map(prevOriginals);
          newMap.delete(fileToRemove.id);
          return newMap;
        });
      }
      return prev.filter((f) => f.contentId !== contentId);
    });
  }, []);

  // Clear all files and reset bot ID
  const clearFiles = useCallback(() => {
    setFiles([]);
    setCreatedBotId(null);
    setOriginalFiles(new Map());
  }, []);

  // Calculate total character count
  const totalCharacterCount = files
    .filter((f) => f.status === 'ready' && f.preview)
    .reduce((acc, f) => acc + (f.preview?.length || 0), 0);

  // Re-process a PDF with AI extraction
  const processWithAI = useCallback(
    async (fileId: string) => {
      const originalFile = originalFiles.get(fileId);
      const existingFile = files.find((f) => f.id === fileId);

      if (!originalFile || !existingFile) {
        console.error('[useFileUpload] Cannot process with AI: file not found');
        return;
      }

      // Only process PDFs
      if (!originalFile.type.includes('pdf')) {
        console.error('[useFileUpload] Cannot process with AI: not a PDF');
        return;
      }

      // Delete the old content first if it exists
      if (existingFile.contentId && createdBotId) {
        try {
          await fetch(`/api/bots/${createdBotId}/content/${existingFile.contentId}`, {
            method: 'DELETE',
          });
        } catch (error) {
          console.error('[useFileUpload] Failed to delete old content before AI re-processing:', error);
        }
      }

      // Clear contentId before re-processing (onMutate will set status to ai-processing)
      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileId ? { ...f, contentId: undefined } : f
        )
      );

      // Re-upload with AI flag (onMutate handles status update)
      uploadMutation.mutate({ file: originalFile, fileId, useAI: true });
    },
    [originalFiles, files, createdBotId, uploadMutation]
  );

  return {
    files,
    isUploading: uploadMutation.isPending,
    uploadFiles,
    handleRejectedFiles,
    removeFile,
    removeByContentId,
    clearFiles,
    totalCharacterCount,
    botId: createdBotId,
    processWithAI,
  };
}

// Export types
export type { FileUploadResponse, FileUploadError };
